import {isSerializablePlainData, isSerializablePlainRecord} from '../domain/SerializablePlainData';
import type {
    SketchConstraintState,
    SketchConstraintTargetRef,
    SketchDimensionDrivingState,
    SketchDimensionState,
    SketchEdgeState,
    SketchPointState,
    SketchRelationRuntimeState,
    SketchSolveStatus,
    SketchSolverConflictKind,
    SketchSolverConflictResolutionCandidate,
    SketchSolverConflictState,
    SketchSolverDiagnostics,
    SketchSolverIslandSnapshot,
    SketchSolverRecord,
    SketchSolverVector2,
    SketchSolverVector3,
    SolverBranchHint,
    SolverParameterEntry,
    SolverParameterVector,
    SolverSolveSeed,
} from './SketchSolverRecordTypes';

/**
 * SolidWorks 草图求解状态全集。
 *
 * 设计说明：
 * 这里保留 over-defined / no-solution / invalid-solution 等非成功态，是为了让历史系统恢复
 * SolidWorks 可见的诊断状态，而不是把这些状态当成失败事务丢弃。
 */
const SKETCH_SOLVE_STATUS_VALUES: readonly SketchSolveStatus[] = [
    'under-defined',
    'fully-defined',
    'over-defined',
    'no-solution',
    'invalid-solution',
    'partial-solved',
    'unknown',
];

/**
 * 草图尺寸驱动角色全集。
 */
const SKETCH_DIMENSION_DRIVING_STATE_VALUES: readonly SketchDimensionDrivingState[] = [
    'driving',
    'driven',
];

/**
 * 草图几何关系运行状态全集。
 */
const SKETCH_RELATION_RUNTIME_STATE_VALUES: readonly SketchRelationRuntimeState[] = [
    'satisfied',
    'suppressed',
    'dangling',
    'conflicting',
    'not-solved',
    'external',
    'locked',
    'broken',
];

/**
 * 草图求解冲突类型全集。
 */
const SKETCH_SOLVER_CONFLICT_KIND_VALUES: readonly SketchSolverConflictKind[] = [
    'over-defined',
    'not-solved',
    'dangling',
    'invalid-solution',
    'redundant',
    'unknown',
];

/**
 * 求解种子来源全集。
 */
const SOLVER_SEED_SOURCE_VALUES: readonly SolverSolveSeed['source'][] = [
    'before-geometry',
    'last-stable',
    'user-drag',
    'manual',
];

/**
 * 判断值是否为有限数值。
 *
 * @param value 待判断值。
 * @returns 值是否为有限数值。
 */
function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断值是否为布尔值或未定义。
 *
 * @param value 待判断值。
 * @returns 值是否为布尔值或未定义。
 */
function isOptionalBoolean(value: unknown): value is boolean | undefined {
    return value === undefined || typeof value === 'boolean';
}

/**
 * 判断值是否为字符串或未定义。
 *
 * @param value 待判断值。
 * @returns 值是否为字符串或未定义。
 */
function isOptionalString(value: unknown): value is string | undefined {
    return value === undefined || typeof value === 'string';
}

/**
 * 判断值是否为有限数值或未定义。
 *
 * @param value 待判断值。
 * @returns 值是否为有限数值或未定义。
 */
function isOptionalFiniteNumber(value: unknown): value is number | undefined {
    return value === undefined || isFiniteNumber(value);
}

/**
 * 判断值是否为字符串数组。
 *
 * @param value 待判断值。
 * @returns 值是否为字符串数组。
 */
function isStringArray(value: unknown): value is readonly string[] {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

/**
 * 判断值是否为二维有限数值向量。
 *
 * @param value 待判断值。
 * @returns 值是否为二维有限数值向量。
 */
export function isSketchSolverVector2(value: unknown): value is SketchSolverVector2 {
    return Array.isArray(value)
        && value.length === 2
        && value.every(isFiniteNumber);
}

/**
 * 判断值是否为三维有限数值向量。
 *
 * @param value 待判断值。
 * @returns 值是否为三维有限数值向量。
 */
export function isSketchSolverVector3(value: unknown): value is SketchSolverVector3 {
    return Array.isArray(value)
        && value.length === 3
        && value.every(isFiniteNumber);
}

/**
 * 对字符串列表去重并稳定排序。
 *
 * @param values 原始字符串列表。
 * @returns 去重后的稳定排序列表。
 */
export function sortUniqueStrings(values: readonly string[]): readonly string[] {
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

/**
 * 创建 solver island 稳定 ID。
 *
 * 设计说明：
 * 1. 该 ID 是历史事务的“求解影响域身份”，不强制等于某个 before 或 after 快照的当前成员集合；
 * 2. 新增 / 删除约束或尺寸时，应传入 before/after 的 union scope，保证同一事务的 before/after
 *    共用一个 islandId；
 * 3. 后续真实 solver 若有自己的 island ID，也必须保证同一事务 before/after 内稳定。
 *
 * @param sketchId 草图 ID。
 * @param memberIds 求解影响域内实体、点、约束、尺寸等成员 ID。
 * @returns 稳定 solver island ID。
 */
export function createSketchSolverIslandId(
    sketchId: string,
    memberIds: readonly string[],
): string {
    const normalizedMembers = sortUniqueStrings(memberIds);
    const memberSegment = normalizedMembers.length > 0
        ? normalizedMembers.map(encodeURIComponent).join(',')
        : 'empty';

    return `solver-island:${encodeURIComponent(sketchId)}:${memberSegment}`;
}

/**
 * 创建空 solver 参数向量。
 *
 * @returns 空 solver 参数向量。
 */
export function createEmptySolverParameterVector(): SolverParameterVector {
    return {
        entries: [],
    };
}

/**
 * 判断值是否为 solver 参数项。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver 参数项。
 */
export function isSolverParameterEntry(value: unknown): value is SolverParameterEntry {
    const entry = value as SolverParameterEntry;

    return typeof entry === 'object'
        && entry !== null
        && typeof entry.parameterId === 'string'
        && isFiniteNumber(entry.value)
        && isOptionalString(entry.entityId)
        && isOptionalString(entry.dimensionId)
        && isOptionalString(entry.role);
}

/**
 * 判断值是否为 solver 参数向量。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver 参数向量。
 */
export function isSolverParameterVector(value: unknown): value is SolverParameterVector {
    const vector = value as SolverParameterVector;

    return typeof vector === 'object'
        && vector !== null
        && Array.isArray(vector.entries)
        && vector.entries.every(isSolverParameterEntry)
        && isOptionalString(vector.coordinateSystem)
        && isOptionalFiniteNumber(vector.revision);
}

/**
 * 判断值是否为 solver 求解种子。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver 求解种子。
 */
export function isSolverSolveSeed(value: unknown): value is SolverSolveSeed {
    const seed = value as SolverSolveSeed;

    return typeof seed === 'object'
        && seed !== null
        && typeof seed.seedId === 'string'
        && SOLVER_SEED_SOURCE_VALUES.includes(seed.source)
        && isSolverParameterVector(seed.parameterVector);
}

/**
 * 判断值是否为多解分支提示。
 *
 * @param value 待判断值。
 * @returns 值是否为多解分支提示。
 */
export function isSolverBranchHint(value: unknown): value is SolverBranchHint {
    const hint = value as SolverBranchHint;

    return typeof hint === 'object'
        && hint !== null
        && typeof hint.hintId === 'string'
        && typeof hint.hintKind === 'string'
        && isStringArray(hint.targetIds)
        && isSerializablePlainData(hint.value);
}

/**
 * 判断值是否为草图点状态。
 *
 * @param value 待判断值。
 * @returns 值是否为草图点状态。
 */
export function isSketchPointState(value: unknown): value is SketchPointState {
    const state = value as SketchPointState;

    return typeof state === 'object'
        && state !== null
        && typeof state.pointId === 'string'
        && isOptionalString(state.entityId)
        && isOptionalString(state.role)
        && isSketchSolverVector2(state.localPosition)
        && (state.worldPosition === undefined || isSketchSolverVector3(state.worldPosition))
        && isOptionalBoolean(state.fixed);
}

/**
 * 判断值是否为草图边或曲线状态。
 *
 * @param value 待判断值。
 * @returns 值是否为草图边或曲线状态。
 */
export function isSketchEdgeState(value: unknown): value is SketchEdgeState {
    const state = value as SketchEdgeState;

    return typeof state === 'object'
        && state !== null
        && typeof state.entityId === 'string'
        && typeof state.entityKind === 'string'
        && isStringArray(state.pointIds)
        && isSerializablePlainRecord(state.geometry)
        && isOptionalBoolean(state.construction);
}

/**
 * 判断值是否为草图几何关系状态。
 *
 * @param value 待判断值。
 * @returns 值是否为草图几何关系状态。
 */
/**
 * 判断值是否为草图约束直接目标引用。
 *
 * @param value 待判断值。
 * @returns 值是否为草图约束直接目标引用。
 */
function isSketchConstraintTargetRef(value: unknown): value is SketchConstraintTargetRef {
    const targetRef = value as SketchConstraintTargetRef;

    return typeof targetRef === 'object'
        && targetRef !== null
        && (
            targetRef.targetKind === 'entity'
            || targetRef.targetKind === 'point'
            || targetRef.targetKind === 'reference'
        )
        && typeof targetRef.targetId === 'string'
        && targetRef.targetId.length > 0;
}

export function isSketchConstraintState(value: unknown): value is SketchConstraintState {
    const state = value as SketchConstraintState;

    return typeof state === 'object'
        && state !== null
        && typeof state.constraintId === 'string'
        && isOptionalFiniteNumber(state.runtimeId)
        && typeof state.relationKind === 'string'
        && (
            state.targetRefs === undefined
            || (Array.isArray(state.targetRefs) && state.targetRefs.every(isSketchConstraintTargetRef))
        )
        && isStringArray(state.targetEntityIds)
        && isStringArray(state.targetPointIds)
        && Array.isArray(state.states)
        && state.states.every((runtimeState) => SKETCH_RELATION_RUNTIME_STATE_VALUES.includes(runtimeState))
        && isOptionalFiniteNumber(state.numericValue)
        && (state.branchHint === undefined || isSerializablePlainData(state.branchHint))
        && (state.formulaPayload === undefined || isSerializablePlainData(state.formulaPayload));
}

/**
 * 判断值是否为草图尺寸状态。
 *
 * @param value 待判断值。
 * @returns 值是否为草图尺寸状态。
 */
export function isSketchDimensionState(value: unknown): value is SketchDimensionState {
    const state = value as SketchDimensionState;

    return typeof state === 'object'
        && state !== null
        && typeof state.dimensionId === 'string'
        && typeof state.dimensionKind === 'string'
        && SKETCH_DIMENSION_DRIVING_STATE_VALUES.includes(state.drivingState)
        && isFiniteNumber(state.value)
        && isOptionalFiniteNumber(state.measuredValue)
        && typeof state.unit === 'string'
        && isStringArray(state.targetEntityIds)
        && isStringArray(state.targetPointIds)
        && (state.displayPosition === undefined || isSketchSolverVector3(state.displayPosition))
        && isOptionalBoolean(state.suppressed)
        && isOptionalBoolean(state.dangling)
        && isOptionalBoolean(state.conflicting)
        && isOptionalString(state.parameterId);
}

/**
 * 判断值是否为冲突修复候选。
 *
 * @param value 待判断值。
 * @returns 值是否为冲突修复候选。
 */
export function isSketchSolverConflictResolutionCandidate(
    value: unknown,
): value is SketchSolverConflictResolutionCandidate {
    const candidate = value as SketchSolverConflictResolutionCandidate;

    return typeof candidate === 'object'
        && candidate !== null
        && typeof candidate.candidateId === 'string'
        && typeof candidate.label === 'string'
        && (candidate.deleteConstraintIds === undefined || isStringArray(candidate.deleteConstraintIds))
        && (candidate.deleteDimensionIds === undefined || isStringArray(candidate.deleteDimensionIds))
        && (candidate.suppressConstraintIds === undefined || isStringArray(candidate.suppressConstraintIds))
        && (
            candidate.convertToDrivenDimensionIds === undefined
            || isStringArray(candidate.convertToDrivenDimensionIds)
        );
}

/**
 * 判断值是否为 solver 冲突状态。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver 冲突状态。
 */
export function isSketchSolverConflictState(value: unknown): value is SketchSolverConflictState {
    const state = value as SketchSolverConflictState;

    return typeof state === 'object'
        && state !== null
        && typeof state.conflictId === 'string'
        && SKETCH_SOLVER_CONFLICT_KIND_VALUES.includes(state.conflictKind)
        && isStringArray(state.entityIds)
        && isStringArray(state.constraintIds)
        && isStringArray(state.dimensionIds)
        && isOptionalString(state.message)
        && (
            state.resolutionCandidates === undefined
            || (
                Array.isArray(state.resolutionCandidates)
                && state.resolutionCandidates.every(isSketchSolverConflictResolutionCandidate)
            )
        );
}

/**
 * 判断值是否为 solver 诊断信息。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver 诊断信息。
 */
export function isSketchSolverDiagnostics(value: unknown): value is SketchSolverDiagnostics {
    const diagnostics = value as SketchSolverDiagnostics;

    return typeof diagnostics === 'object'
        && diagnostics !== null
        && isOptionalFiniteNumber(diagnostics.residual)
        && isOptionalFiniteNumber(diagnostics.iterationCount)
        && isOptionalString(diagnostics.failureReason)
        && (diagnostics.details === undefined || isSerializablePlainRecord(diagnostics.details));
}

/**
 * 判断两个字符串列表是否为同一个集合。
 *
 * @param left 左侧字符串列表。
 * @param right 右侧字符串列表。
 * @returns 两个字符串列表是否为同一个集合。
 */
function areStringSetsEqual(
    left: readonly string[],
    right: readonly string[],
): boolean {
    return JSON.stringify(sortUniqueStrings(left)) === JSON.stringify(sortUniqueStrings(right));
}

/**
 * 判断待检查 ID 是否都存在于允许集合内。
 *
 * @param ids 待检查 ID。
 * @param allowedIds 允许集合。
 * @returns 待检查 ID 是否都存在于允许集合内。
 */
function areIdsKnown(
    ids: readonly string[],
    allowedIds: ReadonlySet<string>,
): boolean {
    return ids.every((id) => allowedIds.has(id));
}

/**
 * 判断参数向量中的实体与尺寸引用是否都存在于快照成员内。
 *
 * @param vector 参数向量。
 * @param entityIds 已知实体 ID。
 * @param dimensionIds 已知尺寸 ID。
 * @returns 参数引用是否完整。
 */
function areParameterVectorReferencesKnown(
    vector: SolverParameterVector | undefined,
    entityIds: ReadonlySet<string>,
    dimensionIds: ReadonlySet<string>,
): boolean {
    if (!vector) {
        return true;
    }

    return vector.entries.every((entry) => {
        const entityKnown = entry.entityId === undefined || entityIds.has(entry.entityId);
        const dimensionKnown = entry.dimensionId === undefined || dimensionIds.has(entry.dimensionId);

        return entityKnown && dimensionKnown;
    });
}

/**
 * 从多个参数向量中提取参数 ID 集合。
 *
 * @param vectors 参数向量列表。
 * @returns 参数 ID 集合。
 */
function createParameterIdSet(
    vectors: readonly (SolverParameterVector | undefined)[],
): ReadonlySet<string> {
    return new Set(vectors.flatMap((vector) => {
        return vector?.entries.map((entry) => entry.parameterId) ?? [];
    }));
}

/**
 * 判断尺寸状态声明的 parameterId 是否能在参数向量中找到。
 *
 * @param snapshot solver island 快照。
 * @returns 尺寸参数引用是否完整。
 */
function areDimensionParameterIdsKnown(snapshot: SketchSolverIslandSnapshot): boolean {
    const parameterIds = createParameterIdSet([
        snapshot.parameterVector,
        snapshot.lastStableParameterVector,
        snapshot.solveSeed?.parameterVector,
    ]);

    return snapshot.dimensionStates.every((dimensionState) => {
        return dimensionState.parameterId === undefined || parameterIds.has(dimensionState.parameterId);
    });
}

/**
 * 判断多解分支提示引用是否都存在于快照成员内。
 *
 * @param branchHints 多解分支提示。
 * @param knownIds 已知成员 ID。
 * @returns 分支提示引用是否完整。
 */
function areBranchHintReferencesKnown(
    branchHints: readonly SolverBranchHint[],
    knownIds: ReadonlySet<string>,
): boolean {
    return branchHints.every((hint) => areIdsKnown(hint.targetIds, knownIds));
}

/**
 * 判断冲突修复候选引用是否都存在于快照成员内。
 *
 * @param conflictStates 冲突状态。
 * @param constraintIds 已知约束 ID。
 * @param dimensionIds 已知尺寸 ID。
 * @returns 冲突修复候选引用是否完整。
 */
function areConflictResolutionCandidateReferencesKnown(
    conflictStates: readonly SketchSolverConflictState[],
    constraintIds: ReadonlySet<string>,
    dimensionIds: ReadonlySet<string>,
): boolean {
    return conflictStates.every((conflictState) => {
        return (conflictState.resolutionCandidates ?? []).every((candidate) => {
            return areIdsKnown(candidate.deleteConstraintIds ?? [], constraintIds)
                && areIdsKnown(candidate.suppressConstraintIds ?? [], constraintIds)
                && areIdsKnown(candidate.deleteDimensionIds ?? [], dimensionIds)
                && areIdsKnown(candidate.convertToDrivenDimensionIds ?? [], dimensionIds);
        });
    });
}

/**
 * 判断 solver island 成员 ID 与状态数组是否完整对齐。
 *
 * @param snapshot solver island 快照。
 * @returns 成员状态是否完整。
 */
function areSnapshotMemberStatesComplete(snapshot: SketchSolverIslandSnapshot): boolean {
    const edgeEntityIds = snapshot.edgeStates.map((state) => state.entityId);
    const pointStateIds = snapshot.pointStates.map((state) => state.pointId);
    const constraintStateIds = snapshot.constraintStates.map((state) => state.constraintId);
    const dimensionStateIds = snapshot.dimensionStates.map((state) => state.dimensionId);

    return areStringSetsEqual(snapshot.entityIds, edgeEntityIds)
        && areStringSetsEqual(snapshot.pointIds, pointStateIds)
        && areStringSetsEqual(snapshot.constraintIds, constraintStateIds)
        && areStringSetsEqual(snapshot.dimensionIds, dimensionStateIds);
}

/**
 * 判断 solver island 快照内部引用是否都能落到本快照成员上。
 *
 * @param snapshot solver island 快照。
 * @returns 快照内部引用是否完整。
 */
function areSnapshotReferencesKnown(snapshot: SketchSolverIslandSnapshot): boolean {
    const entityIds = new Set(snapshot.entityIds);
    const pointIds = new Set(snapshot.pointIds);
    const constraintIds = new Set(snapshot.constraintIds);
    const dimensionIds = new Set(snapshot.dimensionIds);
    const knownIds = new Set([
        ...snapshot.entityIds,
        ...snapshot.pointIds,
        ...snapshot.constraintIds,
        ...snapshot.dimensionIds,
    ]);
    const pointOwnerReferencesKnown = snapshot.pointStates.every((state) => {
        return state.entityId === undefined || entityIds.has(state.entityId);
    });
    const edgeReferencesKnown = snapshot.edgeStates.every((state) => areIdsKnown(state.pointIds, pointIds));
    const constraintReferencesKnown = snapshot.constraintStates.every((state) => {
        const directTargetReferencesKnown = (state.targetRefs ?? []).every((targetRef) => {
            if (targetRef.targetKind === 'reference') {
                // 外部引用由草图文档的稳定引用仓库负责恢复，不属于 island 成员。
                return true;
            }
            return targetRef.targetKind === 'entity'
                ? entityIds.has(targetRef.targetId)
                : pointIds.has(targetRef.targetId);
        });

        return areIdsKnown(state.targetEntityIds, entityIds)
            && areIdsKnown(state.targetPointIds, pointIds)
            && directTargetReferencesKnown;
    });
    const dimensionReferencesKnown = snapshot.dimensionStates.every((state) => {
        return areIdsKnown(state.targetEntityIds, entityIds)
            && areIdsKnown(state.targetPointIds, pointIds);
    });
    const conflictReferencesKnown = snapshot.conflictStates.every((state) => {
        return areIdsKnown(state.entityIds, entityIds)
            && areIdsKnown(state.constraintIds, constraintIds)
            && areIdsKnown(state.dimensionIds, dimensionIds);
    });
    const parameterReferencesKnown = areParameterVectorReferencesKnown(
        snapshot.parameterVector,
        entityIds,
        dimensionIds,
    ) && areParameterVectorReferencesKnown(
        snapshot.lastStableParameterVector,
        entityIds,
        dimensionIds,
    ) && areParameterVectorReferencesKnown(
        snapshot.solveSeed?.parameterVector,
        entityIds,
        dimensionIds,
    );

    return edgeReferencesKnown
        && pointOwnerReferencesKnown
        && constraintReferencesKnown
        && dimensionReferencesKnown
        && conflictReferencesKnown
        && parameterReferencesKnown
        && areDimensionParameterIdsKnown(snapshot)
        && areBranchHintReferencesKnown(snapshot.branchHints, knownIds)
        && areConflictResolutionCandidateReferencesKnown(snapshot.conflictStates, constraintIds, dimensionIds);
}

/**
 * 判断值是否为 solver island 快照。
 *
 * @param value 待判断值。
 * @returns 值是否为 solver island 快照。
 */
export function isSketchSolverIslandSnapshot(value: unknown): value is SketchSolverIslandSnapshot {
    const snapshot = value as SketchSolverIslandSnapshot;

    return typeof snapshot === 'object'
        && snapshot !== null
        && typeof snapshot.islandId === 'string'
        && typeof snapshot.sketchId === 'string'
        && isStringArray(snapshot.entityIds)
        && isStringArray(snapshot.pointIds)
        && isStringArray(snapshot.constraintIds)
        && isStringArray(snapshot.dimensionIds)
        && Array.isArray(snapshot.pointStates)
        && snapshot.pointStates.every(isSketchPointState)
        && Array.isArray(snapshot.edgeStates)
        && snapshot.edgeStates.every(isSketchEdgeState)
        && Array.isArray(snapshot.constraintStates)
        && snapshot.constraintStates.every(isSketchConstraintState)
        && Array.isArray(snapshot.dimensionStates)
        && snapshot.dimensionStates.every(isSketchDimensionState)
        && isSolverParameterVector(snapshot.parameterVector)
        && (snapshot.solveSeed === undefined || isSolverSolveSeed(snapshot.solveSeed))
        && Array.isArray(snapshot.branchHints)
        && snapshot.branchHints.every(isSolverBranchHint)
        && SKETCH_SOLVE_STATUS_VALUES.includes(snapshot.solveStatus)
        && Array.isArray(snapshot.conflictStates)
        && snapshot.conflictStates.every(isSketchSolverConflictState)
        && (
            snapshot.lastStableParameterVector === undefined
            || isSolverParameterVector(snapshot.lastStableParameterVector)
        )
        && (snapshot.diagnostics === undefined || isSketchSolverDiagnostics(snapshot.diagnostics))
        && isFiniteNumber(snapshot.capturedAt)
        && isOptionalFiniteNumber(snapshot.revision)
        && areSnapshotMemberStatesComplete(snapshot)
        && areSnapshotReferencesKnown(snapshot);
}

/**
 * 去除 solver island 快照中的采集元数据。
 *
 * @param snapshot solver island 快照。
 * @returns 仅包含模型语义字段的 solver island 快照。
 */
function createSemanticSolverIslandSnapshot(
    snapshot: SketchSolverIslandSnapshot,
): Omit<SketchSolverIslandSnapshot, 'capturedAt' | 'revision'> {
    const {
        capturedAt: _capturedAt,
        revision: _revision,
        ...semanticSnapshot
    } = snapshot;

    return semanticSnapshot;
}

/**
 * 判断 solver island 快照是否模型语义相等。
 *
 * 设计说明：
 * `capturedAt` 和 `revision` 是采集元数据，不代表用户可感知的草图求解结果变化。仅这些字段变化时，
 * 不应生成 solver 历史记录，避免污染模型 undo stack。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 两个快照是否模型语义相等。
 */
export function areSketchSolverIslandSnapshotsEqual(
    left: SketchSolverIslandSnapshot,
    right: SketchSolverIslandSnapshot,
): boolean {
    return JSON.stringify(createSemanticSolverIslandSnapshot(left))
        === JSON.stringify(createSemanticSolverIslandSnapshot(right));
}

/**
 * 判断 solver 记录是否具备一致的 before / after 边界。
 *
 * @param record 待判断记录。
 * @returns 记录是否一致。
 */
export function isSketchSolverRecordConsistent(record: SketchSolverRecord): boolean {
    return record.sketchId === record.before.sketchId
        && record.sketchId === record.after.sketchId
        && record.islandId === record.before.islandId
        && record.islandId === record.after.islandId
        && isSketchSolverIslandSnapshot(record.before)
        && isSketchSolverIslandSnapshot(record.after);
}
